/**
 * React Query hooks for each dashboard widget's data endpoint.
 *
 * Co-located so future widgets follow the same naming + cache-key pattern.
 * staleTime defaults to 60s — widget data is mostly business metrics that
 * don't need second-by-second freshness, but refresh on revisit.
 *
 * Hooks that depend on the date range read it from DashboardRangeContext
 * and include the key in their React Query cache key. Changing the
 * dropdown automatically triggers a refetch with the new range.
 */

import { useQuery, type UseQueryResult } from "react-query";
import {
  fetchAgingReport,
  fetchAppointmentsSummary,
  fetchAppointmentsByType,
  fetchBanners,
  fetchEstimatesSummary,
  fetchInvoicesSummary,
  fetchProjectsList,
  fetchRepairRequests,
  fetchRequestsByType,
  fetchRequestsTrend,
  fetchSchedulesSummary,
  fetchServiceRequestsList,
  fetchTimeOffUpcoming,
} from "./api";
import { useDashboardRange } from "./RangeContext";
import type { DashboardRangeValue } from "./RangeContext";
import type {
  AgingReport,
  AppointmentsByType,
  AppointmentsSummary,
  DashboardBanner,
  EstimatesSummary,
  InvoicesSummary,
  InvoiceStatusFilter,
  ProjectsList,
  RepairRequestsSummary,
  RequestsByType,
  RequestsTrend,
  SchedulesSummary,
  ServiceRequestsList,
  TimeOffUpcoming,
  AgingRangeKey,
} from "./types";

const COMMON = {
  staleTime: 60 * 1000,
  cacheTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
  // Always refetch when a widget remounts. Dashboard data is mutated on other
  // pages (record a payment, create an invoice/request, edit an appointment);
  // returning to the dashboard remounts the widgets, and without this the 60s
  // staleTime would keep serving the pre-change snapshot — the reported
  // "aging report didn't change after a payment". Cached data still renders
  // instantly; the refetch updates it in the background.
  refetchOnMount: "always",
  retry: 1,
} as const;

/**
 * `range.key === "custom"` requires both from + to dates. Hooks gate on
 * this so we don't fire a request that the BE would reject — the user
 * picks "Custom", and the query stays idle until they finish the picker.
 */
function rangeIsReady(range: DashboardRangeValue): boolean {
  if (range.key !== "custom") return true;
  return Boolean(range.fromDate && range.toDate);
}

export const useAppointmentsSummary = (): UseQueryResult<AppointmentsSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "appointments-summary", range],
    () => fetchAppointmentsSummary(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

// Only fetched while the Appointments widget's "Appointment Type" tab is
// active (gated by `enabled`) so the default Status view doesn't pay for it.
export const useAppointmentsByType = (
  enabled: boolean,
): UseQueryResult<AppointmentsByType> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "appointments-by-type", range],
    () => fetchAppointmentsByType(range),
    { ...COMMON, enabled: enabled && rangeIsReady(range) },
  );
};

// Global announcement banners (deployment / downtime notices). No date range:
// the BE returns only those active right now. Independent of RangeContext, so
// this can be used in the home header outside the dashboard grid.
export const useBanners = (): UseQueryResult<DashboardBanner[]> =>
  useQuery(["dashboard", "banners"], fetchBanners, COMMON);

export const useInvoicesSummary = (): UseQueryResult<InvoicesSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "invoices-summary", range],
    () => fetchInvoicesSummary(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useEstimatesSummary = (): UseQueryResult<EstimatesSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "estimates-summary", range],
    () => fetchEstimatesSummary(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useRepairRequests = (): UseQueryResult<RepairRequestsSummary> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "repair-requests", range],
    () => fetchRepairRequests(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useRequestsByType = (): UseQueryResult<RequestsByType> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "requests-by-type", range],
    () => fetchRequestsByType(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useRequestsTrend = (): UseQueryResult<RequestsTrend> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "requests-trend", range],
    () => fetchRequestsTrend(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

// Service Requests + Projects + Schedules don't react to the dashboard
// date dropdown — they show their own upcoming-by-date lists or use a
// fixed `days` window.
export const useServiceRequestsList = (): UseQueryResult<ServiceRequestsList> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "service-requests-list", range],
    () => fetchServiceRequestsList(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useProjectsList = (): UseQueryResult<ProjectsList> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "projects-list", range],
    () => fetchProjectsList(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

export const useSchedulesSummary = (days = 14): UseQueryResult<SchedulesSummary> =>
  useQuery(["dashboard", "schedules-summary", days], () => fetchSchedulesSummary(days), COMMON);

export const useTimeOffUpcoming = (): UseQueryResult<TimeOffUpcoming> => {
  const range = useDashboardRange();
  return useQuery(
    ["dashboard", "time-off-upcoming", range],
    () => fetchTimeOffUpcoming(range),
    { ...COMMON, enabled: rangeIsReady(range) },
  );
};

// The aging widget has its OWN period filter (ESERV2-1378) — independent of
// the page-level range selector: ytd (default) / rolling 12 months / custom.
export const useAgingReport = (
  status: InvoiceStatusFilter = "total",
  range: AgingRangeKey = "ytd",
  customFrom?: string,
  customTo?: string,
): UseQueryResult<AgingReport> => {
  const customReady = range !== "custom" || Boolean(customFrom && customTo);
  return useQuery(
    ["dashboard", "aging-report", status, range, customFrom, customTo],
    () => fetchAgingReport(status, range, customFrom, customTo),
    { ...COMMON, enabled: customReady },
  );
};

/** Helper for widget error display. */
export const formatQueryError = (err: unknown): string =>
  err instanceof Error ? err.message : "Failed to load";
